import { AnalyticsContext } from '@carvana/eventable-clickstream';
import {
  DetailedHTMLProps,
  ElementType,
  HTMLAttributes,
  useContext,
  useEffect,
  useRef
} from 'react';
import { CommonSection } from './Financing.styles';

type SectionProps = DetailedHTMLProps<HTMLAttributes<HTMLElement>, HTMLElement>;

type OwnProps = SectionProps & {
  eventName: string;
  as?: ElementType;
};

export default function Section({
  eventName,
  as: Component = CommonSection,
  ...props
}: OwnProps) {
  const { trackEvent } = useContext(AnalyticsContext);

  const sectionRef = useRef<HTMLElement>(null);
  const hasTrackedRef = useRef(false);

  useEffect(() => {
    if (!sectionRef.current || !eventName) {
      return;
    }

    const element = sectionRef.current;

    // Reset tracking state when element or section name changes
    hasTrackedRef.current = false;

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          // Track only when element comes into view and hasn't been tracked yet
          if (entry.isIntersecting && !hasTrackedRef.current) {
            trackEvent(eventName, { action: 'viewed' });
            hasTrackedRef.current = true;
          }
        });
      },
      {
        // Trigger when at least 10% of the element is visible
        threshold: 0.1,
        // Add some margin to trigger slightly before the element is fully visible
        rootMargin: '0px 0px -10% 0px'
      }
    );

    observer.observe(element);

    return () => {
      observer.unobserve(element);
      observer.disconnect();
    };
  }, [eventName, trackEvent]);

  return <Component {...props} ref={sectionRef} />;
}
